/**
 * Answered questions (D37): when a refusal ASKS, the session remembers what
 * it asked, so a bare reply completes the command.
 *
 * @remarks
 * Three v0 refusals are questions rather than statements — "Open it with
 * what?", "Use it on what?", "Go where?" — and before D37 the natural human
 * answer ("kettle") arrived as an orphan noun and was told no directive was
 * recognized. That is the game asking a question and then failing to hear the
 * reply.
 *
 * The mechanism is deliberately small: park the input that earned the
 * question plus the word that will join an answer to it, then splice
 * (`"open the locker" + "with" + "kettle"`) and run the result through the
 * REAL pipeline. The parser stays the judge — nothing is inferred about what
 * the answer means, and a spliced input that still fails fails honestly.
 *
 * The joiner comes from the intent's DECLARED preposition bindings, never a
 * guess: a vocabulary that binds its tool with `using` gets "using". State
 * lives in `RuntimeState.session`, so the pending question survives save and
 * reload, and replays reproduce the conversation exactly.
 *
 * @packageDocumentation
 */

import type { RuntimeState } from '@orpheus/content-schema';

import { parseCommand } from '@orpheus/parser-engine';

import type { ActionRejection } from '../actions/runtime-action.js';
import type { EngineGameBundle } from '../bundle/engine-game-bundle.js';

import { requireItem } from '../bundle/bundle-lookups.js';
import { speakSlot, type SpokenResponse } from '../defaults/default-responses.js';
import { describeEntityRef } from '../entities/describe-entity-ref.js';
import { clauseCatalogOf } from './clause-settlement.js';
import { buildParseContext } from './parse-context.js';
import { type PendingCompletionQuestion } from './pending-question.js';
import { type PlayerInputOutcome } from './process-player-input.js';

/** What the session is waiting to hear — D79: the completion KIND of the question union. */
export type PendingCompletion = PendingCompletionQuestion;

/**
 * The pending question one refusal earns, if it asked one.
 *
 * @param bundle - The loaded bundle (declared preposition bindings)
 * @param rejection - The refusal just produced
 * @param rawInput - The input that earned it, verbatim
 * @returns The completion to park, or `undefined` when the refusal was a statement
 */
export const pendingCompletionForRejection = (
	bundle: EngineGameBundle,
	rejection: ActionRejection,
	clauseText: string,
): Pick<PendingCompletion, 'clauseText' | 'joiner' | 'prefixesAnswer'> | undefined => {
	switch (rejection.reason.type) {
		case 'missing_direction': {
			// "Go where?" — a bare direction answers it, no joining word.
			return { clauseText, joiner: '' };
		}
		case 'missing_instrument': {
			return { clauseText, joiner: declaredPreposition(bundle, 'open', 'tool') ?? 'with' };
		}
		// D235: "Give the film to whom?" — a bare name answers, joined by the gift verb's own preposition.
		case 'missing_recipient': {
			return { clauseText, joiner: declaredPreposition(bundle, rejection.reason.intentToken, 'indirect_object') ?? 'to' };
		}
		case 'missing_target': {
			return { clauseText, joiner: declaredPreposition(bundle, 'use', 'indirect_object') ?? 'on' };
		}
		default: {
			return undefined;
		}
	}
};

/**
 * Splice a bare answer onto the parked question.
 *
 * @param pendingCompletion - What the session asked
 * @param answerInput - The player's reply, verbatim
 * @returns The input to run through the pipeline
 */
export const spliceAnsweredInput = (pendingCompletion: PendingCompletion, answerInput: string): string => {
	const parts =
		pendingCompletion.prefixesAnswer === true
			? [answerInput, pendingCompletion.joiner, pendingCompletion.clauseText]
			: [pendingCompletion.clauseText, pendingCompletion.joiner, answerInput];

	return parts.filter((part) => part.trim() !== '').join(' ');
};

/**
 * The question a verb-without-its-object earns (D41). "To search what?"
 * invites a noun as plainly as "Open it with what?" does, so it parks the same
 * way and a bare reply completes it.
 *
 * @param outcome - The outcome just produced
 * @param rawInput - Exactly what the player typed
 * @returns The completion to park, or `undefined` when this was not that shape
 */
export const questionForMissingObject = (
	outcome: PlayerInputOutcome,
	rawInput: string,
): PendingCompletion | undefined => {
	if (
		outcome.type !== 'parse_failed' ||
		outcome.diagnostics.failureKind !== 'structure_not_understood' ||
		outcome.diagnostics.understoodIntentToken === undefined ||
		outcome.diagnostics.unmatchedTokens.length > 0
	) {
		return undefined;
	}

	// The verb goes first and the answer follows it: "search" + "locker".
	// F5: park the failing CLAUSE, never the whole input — a bare verb ending
	// a chain ("inspect the benches and take") must not re-run the benches
	// when its noun arrives. The parser names the clause on the diagnostics;
	// the fallback is the single-clause case, where they are the same text.
	// D79: the question's own text is the teach that just spoke — found by its
	// provenance stamp, so the surface shows exactly what the transcript said.
	return {
		askedAtInputOrdinal: outcome.nextState.session.commandHistory.length,
		clauseText: outcome.diagnostics.failedClauseText ?? rawInput,
		joiner: '',
		kind: 'completion',
		questionText:
			outcome.transcriptEntries.findLast((entry) => entry.triggerContext === 'parse_teach')?.text ?? '',
		roomId: outcome.nextState.progression.activeRoomId,
	};
};

/**
 * The line refusing an answer that resolved to nothing (D38) — it names the
 * verb, the thing, and the player's own word, because the player is answering
 * a question and a bare "nothing here answers to that" forgets the question
 * was asked.
 *
 * @param bundle - The loaded bundle
 * @param state - The state before the answer
 * @param pendingCompletion - The parked question
 * @param answerInput - The player's reply, verbatim
 * @returns The contextual spoken response, or `undefined` when the question's own command no longer reads
 */
export const refusalForFailedAnswer = (
	bundle: EngineGameBundle,
	state: RuntimeState,
	pendingCompletion: PendingCompletion,
	answerInput: string,
): SpokenResponse | undefined => {
	// The parked clause is a command that PARSED and was refused for a missing
	// slot, so re-parsing it recovers the verb and the thing it named.
	// D247: read as the player settled it, without what a which-one answer ruled out.
	const askedOutcome = parseCommand(pendingCompletion.clauseText, buildParseContext(bundle, clauseCatalogOf(bundle, state, pendingCompletion.ruledOutRefs)));
	const askedCommand = askedOutcome.type === 'parsed' ? askedOutcome.commands[0] : undefined;
	const objectRef = askedCommand?.directObjectRef ?? askedCommand?.indirectObjectRef;

	if (askedCommand === undefined || objectRef === undefined) {
		return undefined;
	}

	// The TOTAL namer, not a lookup with a fallback name (D232; before it,
	// `requireItem` — every question that could be parked named an item, and
	// a bare noun can now name an actor). A `?? 'it'` here would be a phantom
	// branch no test could enter (engineering_standards §14.1); the namer
	// answers for every kind the catalog holds and says it once.
	return speakSlot(bundle, state, 'answerNotUnderstood', {
		intent: askedCommand.intentSurface.toUpperCase(),
		itemName: describeEntityRef(bundle, objectRef),
		joiner: pendingCompletion.joiner,
		tokens: answerInput.trim(),
		verb: askedCommand.intentSurface.toLowerCase(),
	});
};

/**
 * The in-context refusal for an answer that RESOLVED and was then refused
 * (§3.2, D84 — Calder's verdict). "Open it with what?" answered with
 * `kettle` used to earn the two-objects line ("Those two have nothing to say
 * to each other") — machinery right, register wrong: the player was
 * answering a QUESTION, and the reply answered a different sentence than the
 * one they typed. This override speaks in the question's terms — "The kettle
 * will not open the wall locker." — built from the rejection's OWN item ids
 * (authored names, no quoting) and the asked clause's verb.
 *
 * @param bundle - The loaded bundle
 * @param state - The state before the answer
 * @param pendingCompletion - The parked question the answer served
 * @returns The override the clause walk applies to a stopping rejection (nothing for reasons that are not the object-pair refusal)
 */
export const refusalForRefusedAnswer =
	(
		bundle: EngineGameBundle,
		state: RuntimeState,
		pendingCompletion: PendingCompletion,
	): ((rejection: ActionRejection) => SpokenResponse | undefined) =>
		(rejection) => {
			if (rejection.reason.type !== 'no_matching_interaction') {
				return undefined;
			}

			// The parked clause parsed once already (that is how it earned its
			// question), so re-parsing recovers the verb the question was about.
			const askedOutcome = parseCommand(
				pendingCompletion.clauseText,
				buildParseContext(bundle, clauseCatalogOf(bundle, state, pendingCompletion.ruledOutRefs)),
			);
			const askedCommand = askedOutcome.type === 'parsed' ? askedOutcome.commands[0] : undefined;

			if (askedCommand === undefined) {
				return undefined;
			}

			// R28 (Calder's D102 §2 parity ask): the joiner rides along, so the
			// two answer slots offer the same params and a pack can place the
			// instrument with the correct preposition per verb — his content
			// fix hardcoded `with`, which is safe today; parity is proper.
			return speakSlot(bundle, state, 'answerDoesNotServe', {
				itemName: requireItem(bundle, rejection.reason.targetItemId).name,
				joiner: pendingCompletion.joiner,
				tokens: requireItem(bundle, rejection.reason.instrumentItemId).name,
				verb: askedCommand.intentSurface.toLowerCase(),
			});
		};

/**
 * Settle a resolved answer that was REFUSED (R33 §2/D113): the question
 * closes with the refusal — the player named their choice and the world said
 * no — unless the refusal itself EARNED a fresh question, in which case the
 * new question is the conversation and it parks.
 *
 * @param bundle - The loaded bundle
 * @param outcome - The refused-answer rejection, committed
 * @returns The outcome, with a freshly earned question parked or none at all
 */
export const settleRefusedAnswer = (
	bundle: EngineGameBundle,
	outcome: Extract<PlayerInputOutcome, { type: 'action_rejected' }>,
): PlayerInputOutcome => {
	const reAskedOutcome = withPendingCompletion(bundle, outcome);
	const hasEarnedNewQuestion =
		reAskedOutcome.type === 'action_rejected' && reAskedOutcome.nextState.session.pendingQuestion !== undefined;

	return hasEarnedNewQuestion ? reAskedOutcome : outcome;
};

/**
 * Park the question a refusal just asked, so the next bare reply completes it.
 *
 * @param bundle - The loaded bundle
 * @param outcome - The outcome as produced
 * @returns The outcome, with the question parked on its committed state
 */
export const withPendingCompletion = (bundle: EngineGameBundle, outcome: PlayerInputOutcome): PlayerInputOutcome => {
	if (outcome.type !== 'action_rejected') {
		return outcome;
	}

	// The question remembers the CLAUSE that earned it (F5) — after a splice
	// that is the spliced clause, so "open the locker" → "with what?" →
	// "kettle" → "…with what?" still chains coherently. Never the whole
	// input: a parked whole input re-ran its prefix when the answer spliced,
	// and a prefix clause with effects fired twice (Calder's grille repro —
	// the correct answer was refused in her voice about a different action,
	// and the locker never opened). Since R21 paired the rejection with its
	// clause at the walk, every rejected outcome carries the clause — the old
	// whole-input fallback is gone because the state it covered cannot exist.
	const parked = pendingCompletionForRejection(bundle, outcome.rejection, outcome.rejectedClauseText);

	if (parked === undefined) {
		return outcome;
	}

	return {
		...outcome,
		nextState: {
			...outcome.nextState,
			session: {
				...outcome.nextState.session,
				// D79: the park carries its spoken text (the refusal's own line,
				// already resolved — R15) and its context.
				pendingQuestion: {
					...parked,
					askedAtInputOrdinal: outcome.nextState.session.commandHistory.length,
					kind: 'completion',
					questionText: outcome.rejection.narratives[0]?.text ?? '',
					roomId: outcome.nextState.progression.activeRoomId,
				},
			},
		},
	};
};

/**
 * The preposition a vocabulary DECLARED for one intent's role — authored, so
 * a pack that binds its tool with `using` is answered in its own words.
 *
 * @param bundle - The loaded bundle
 * @param intentText - The canonical intent whose bindings to read
 * @param role - The role whose preposition is wanted
 * @returns The declared preposition, or `undefined` when the intent declares none
 */
const declaredPreposition = (
	bundle: EngineGameBundle,
	intentText: string,
	role: 'indirect_object' | 'tool',
): string | undefined => {
	// A WALK, not `intentsByToken.get(intentText)`. `get` hands back
	// `CompiledIntent | undefined`, and the undefined arm cannot happen here:
	// this helper is module-internal and only ever asked for `open`/`tool` and
	// `use`/`indirect_object`, and those refusals exist only because
	// `narrowParsedCommand` dispatched on an `open`/`use` command — which the
	// grammar could not have produced unless the token was in this very lexicon.
	// A guard for it is a phantom branch no test can enter, and §14.1 says
	// restructure rather than suppress. The walk needs no guard because both
	// misses are the same answer: an intent that is not the wanted one and an
	// intent that binds no such role both yield none, and the caller supplies
	// the engine's built-in joiner (D37).
	const declaredBindings = [...bundle.compiledLexicon.intentsByToken]
		.filter(([token]) => token === intentText)
		.flatMap(([, intent]) => [...intent.prepositionRoles]);

	return declaredBindings.find(([, boundRole]) => boundRole === role)?.[0];
};
